import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as styles from './SearchPlatformExternalPlatformTab.module.scss';
import SearchPlatformTab from './SearchPlatformTab';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import SVGIcon from '../components/SVGIcon';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import { UIActions } from '../ui/uiSlice';

interface SearchPlatformExternalPlatformTabProps {
  active: boolean;
  encourageInstallation: boolean;
  platformKey: string;
  setPlatform: (platform?: string) => void;
  className?: string;
  activeClassName?: string;
  iconHeight: number;
  query: string;
  skip: boolean;
  loadPlatformData: (
    query: string,
    platform: string,
  ) => Promise<{ count: number | null }>;
}

const SearchPlatformExternalPlatformTab = ({
  active,
  encourageInstallation,
  platformKey,
  setPlatform,
  className,
  activeClassName,
  iconHeight,
  query,
  skip,
  loadPlatformData,
}: SearchPlatformExternalPlatformTabProps) => {
  const dispatch = useAppDispatch();
  const loadPlatformDataRef = useRef(loadPlatformData);
  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const [count, setCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    loadPlatformDataRef.current = loadPlatformData;
  });

  useEffect(() => {
    if (skip) {
      return undefined;
    }
    let shouldIgnore = false;
    setCount(null);
    loadPlatformDataRef.current(query, platformKey).then((data) => {
      if (!shouldIgnore) {
        setCount(data.count);
      }
    });
    return () => {
      shouldIgnore = true;
    };
  }, [skip, query, platformKey]);

  if (!platformInfo) return null;
  const { name, icon } = platformInfo;

  const handleDismiss = (platform: string) => {
    dispatch(UIActions.dismissSearchConnectionPrompt(platform));
    if (active) {
      setPlatform(undefined);
    }
  };

  return (
    <SearchPlatformTab
      key={platformKey}
      onClick={() => setPlatform(platformKey)}
      className={className}
      active={active}
      activeClassName={activeClassName}
      icon={icon && <ExternalBrandLogo {...icon} height={iconHeight} />}
      name={name === 'Google Drive' ? 'Drive' : name}
      meta={
        encourageInstallation ? (
          <Button
            className={styles.dismissButton}
            onClick={(e) => {
              e.preventDefault();
              handleDismiss(platformKey);
            }}
          >
            <SVGIcon size={10}>{clearIcon}</SVGIcon>
          </Button>
        ) : (
          <>{count}</>
        )
      }
    />
  );
};

export default SearchPlatformExternalPlatformTab;
